import { useRef, useState, type ChangeEvent, type DragEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import StepIndicator from './StepIndicator';
import BulkUploadErrorBoundary from './BulkUploadErrorBoundary';

/**
 * THE shared 5-step CSV import shell: upload, check, preview, apply, done.
 *
 * The shell owns the file handling (pick or drop, `.csv` only, 5 MB cap, read),
 * the step machine, the spinners, the close-lock while a request runs, the
 * reset on close, the error boundary and the dialog chrome. The caller owns
 * the meaning: `parse` turns the file text into its data (or file errors),
 * `check` asks the server what would happen (a dry run), `apply` does it, and
 * the `render*` callbacks draw the preview and the result.
 */

const MAX_CSV_BYTES = 5 * 1024 * 1024;

const STEPS = ['upload', 'check', 'preview', 'apply', 'done'] as const;
type Step = (typeof STEPS)[number];

export type CsvImportParseResult<TData> = { data: TData } | { errors: string[] };

export interface CsvImportWizardProps<TData, TPreview, TResult> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description: ReactNode;
  /** Upload-step body above the drop zone (how-to, template link). */
  intro?: ReactNode;
  parse: (text: string, file: File) => CsvImportParseResult<TData>;
  check: (data: TData) => Promise<TPreview>;
  renderPreview: (preview: TPreview, data: TData) => ReactNode;
  canApply: (preview: TPreview) => boolean;
  applyLabel: (preview: TPreview) => ReactNode;
  apply: (data: TData, preview: TPreview) => Promise<TResult>;
  renderResult: (result: TResult) => ReactNode;
  /** Shows "View in Logs" on the done step. */
  logsHref?: string;
  /**
   * Where focus goes when the dialog closes. The wizard has no Radix trigger
   * (callers open it from state), so without this Radix drops focus on
   * <body>: pass a handler that calls `event.preventDefault()` and focuses
   * the button that opened it.
   */
  onCloseAutoFocus?: (event: Event) => void;
}

/** FileReader, not `Blob.text()`: one code path that also runs under jsdom. */
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file);
  });

const errorDescription = (error: unknown) => (error instanceof Error ? error.message : undefined);

// A function DECLARATION so the generics survive at every call site.
export function CsvImportWizard<TData, TPreview, TResult>({
  open,
  onClose,
  title,
  description,
  intro,
  parse,
  check,
  renderPreview,
  canApply,
  applyLabel,
  apply,
  renderResult,
  logsHref,
  onCloseAutoFocus,
}: CsvImportWizardProps<TData, TPreview, TResult>) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [data, setData] = useState<TData | null>(null);
  const [preview, setPreview] = useState<TPreview | null>(null);
  const [result, setResult] = useState<TResult | null>(null);

  const busy = step === 'check' || step === 'apply';

  const reset = () => {
    setStep('upload');
    setFileErrors([]);
    setData(null);
    setPreview(null);
    setResult(null);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const runCheck = async (toCheck: TData) => {
    setStep('check');
    try {
      setPreview(await check(toCheck));
      setStep('preview');
    } catch (error) {
      toast.error(t('csvImportWizard.checkFailed'), {
        description: errorDescription(error),
        duration: Infinity,
      });
      reset();
    }
  };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setFileErrors([]);
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileErrors([t('csvImportWizard.notCsv')]);
      return;
    }
    if (file.size > MAX_CSV_BYTES) {
      setFileErrors([t('csvImportWizard.tooLarge')]);
      return;
    }
    let text: string;
    try {
      text = await readFileAsText(file);
    } catch {
      setFileErrors([t('csvImportWizard.readFailed')]);
      return;
    }
    const parsed = parse(text, file);
    if ('errors' in parsed) {
      setFileErrors(parsed.errors);
      return;
    }
    setData(parsed.data);
    await runCheck(parsed.data);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the same file again fires a change.
    event.target.value = '';
    void handleFile(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    void handleFile(event.dataTransfer.files[0]);
  };

  const handleApply = async () => {
    if (data === null || preview === null) return;
    setStep('apply');
    try {
      setResult(await apply(data, preview));
      setStep('done');
    } catch (error) {
      toast.error(t('csvImportWizard.applyFailed'), {
        description: errorDescription(error),
        duration: Infinity,
      });
      // Part of the apply may be saved: show the server's truth, not the old preview.
      await runCheck(data);
    }
  };

  const steps = STEPS.map((key) => ({ key, label: t(`csvImportWizard.step.${key}`) }));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" onCloseAutoFocus={onCloseAutoFocus}>
        <BulkUploadErrorBoundary onReset={reset}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <div className="py-3">
            <StepIndicator steps={steps} currentStep={step} />
          </div>

          <div className="min-h-[160px] space-y-4">
            {step === 'upload' && (
              <>
                {intro}
                {fileErrors.length > 0 && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <ul className="list-disc list-inside space-y-1 text-sm [overflow-wrap:anywhere]">
                        {fileErrors.map((message, index) => (
                          <li key={index}>{message}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
                <div
                  data-testid="csv-import-dropzone"
                  className="rounded-md border-2 border-dashed p-6 text-center transition-colors hover:border-threadheads-purple/40"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleDrop}
                >
                  <Upload className="mx-auto mb-2 h-6 w-6 text-muted-foreground" aria-hidden="true" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    aria-label={t('csvImportWizard.chooseFile')}
                    onChange={handleInputChange}
                  />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                    {t('csvImportWizard.chooseFile')}
                  </Button>
                  <p className="mt-2 text-xs text-muted-foreground">{t('csvImportWizard.dropHint')}</p>
                </div>
              </>
            )}

            {busy && (
              <div className="flex flex-col items-center justify-center gap-3 py-10" role="status">
                <Loader2 className="h-7 w-7 animate-spin text-threadheads-purple" aria-hidden="true" />
                <p className="text-sm font-medium">
                  {step === 'check' ? t('csvImportWizard.checking') : t('csvImportWizard.applying')}
                </p>
                <p className="text-xs text-muted-foreground">{t('csvImportWizard.keepOpen')}</p>
              </div>
            )}

            {step === 'preview' && preview !== null && data !== null && renderPreview(preview, data)}

            {step === 'done' && result !== null && renderResult(result)}
          </div>

          <DialogFooter className="flex-wrap gap-2 pt-2">
            {step === 'upload' && (
              <Button variant="outline" onClick={handleClose}>
                {t('cancel')}
              </Button>
            )}
            {step === 'preview' && preview !== null && (
              <>
                <Button variant="outline" onClick={reset}>
                  {t('csvImportWizard.chooseAnother')}
                </Button>
                <Button
                  onClick={() => void handleApply()}
                  disabled={!canApply(preview)}
                  className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                >
                  {applyLabel(preview)}
                </Button>
              </>
            )}
            {step === 'done' && (
              <>
                {logsHref ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      handleClose();
                      navigate(logsHref);
                    }}
                  >
                    {t('csvImportWizard.viewInLogs')}
                  </Button>
                ) : null}
                <Button onClick={handleClose}>{t('close')}</Button>
              </>
            )}
          </DialogFooter>
        </BulkUploadErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}
